<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Icon from 'next/icon/Icon.vue';
import SuperAdminLink from './SuperAdminLink.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  sections: { type: Array, default: () => [] },
  showSuperAdmin: { type: Boolean, default: false },
  routeSections: { type: Array, default: () => [] },
  quickItems: { type: Array, default: () => [] },
  selectedSection: { type: String, default: '' },
  panelOpen: { type: Boolean, default: true },
});

const emit = defineEmits(['togglePanel', 'select']);

const route = useRoute();
const router = useRouter();
const { isAllowed, resolvePath } = useSidebarContext();
const panelToggleLabel = computed(() =>
  props.panelOpen ? 'Recolher menu' : 'Expandir menu'
);

const visibleChildren = item =>
  (item.children || [])
    .flatMap(child => child.children || child)
    .filter(child => child.show !== false && child.to && isAllowed(child.to));

const isItemVisible = item => {
  if (item.show === false) return false;
  if (item.to) return isAllowed(item.to);
  return visibleChildren(item).length > 0;
};

const visibleSections = computed(() =>
  props.sections.filter(section => section.items.some(isItemVisible))
);
const visibleRouteSections = computed(() =>
  (props.routeSections.length ? props.routeSections : props.sections).filter(
    section => section.items.some(isItemVisible)
  )
);
const visibleQuickItems = computed(() =>
  props.quickItems.filter(isItemVisible)
);

const itemIsActive = item => {
  const items = item.to
    ? [item, ...visibleChildren(item)]
    : visibleChildren(item);

  return items.some(candidate => {
    if (candidate.activeOn?.includes(route.name)) return true;
    if (!candidate.to) return false;

    const candidatePath = resolvePath(candidate.to);
    return (
      route.path === candidatePath || route.path.startsWith(`${candidatePath}/`)
    );
  });
};

const quickItemIsSelected = item => {
  const linkedSection = item.sectionName
    ? visibleRouteSections.value.find(
        section => section.name === item.sectionName
      )
    : null;
  const matchesCurrentRoute =
    itemIsActive(item) || linkedSection?.items.some(itemIsActive);

  return (
    matchesCurrentRoute &&
    (!item.sectionName ||
      !props.selectedSection ||
      props.selectedSection === item.sectionName)
  );
};
const sectionIsActive = section => section.items.some(itemIsActive);
const sectionIsSelected = section =>
  props.selectedSection === section.name ||
  (section.direct && sectionIsActive(section));
const openQuickItem = item => {
  if (item.sectionName) {
    emit('select', item.sectionName);
    if (!props.panelOpen) emit('togglePanel');
  } else {
    emit('select', '');
    if (props.panelOpen) emit('togglePanel');
  }
  router.push(item.to);
};

const selectSection = section => {
  emit('select', section.name);
  if (!props.panelOpen) emit('togglePanel');
};

const selectDirectSection = () => {
  emit('select', '');
  if (props.panelOpen) emit('togglePanel');
};

// Items that depend on async data (e.g. WhatsApp Cloud inboxes) appear later,
// so the active module is re-evaluated when the visible items change too.
const visibleItemsKey = computed(() =>
  visibleRouteSections.value
    .flatMap(section => section.items.filter(isItemVisible))
    .map(item => item.name)
    .join('|')
);

watch(
  [() => route.fullPath, visibleItemsKey],
  () => {
    const routeSection =
      visibleRouteSections.value.find(
        section =>
          section.name === props.selectedSection && sectionIsActive(section)
      ) || visibleRouteSections.value.find(sectionIsActive);
    if (routeSection) {
      emit('select', routeSection.direct ? '' : routeSection.name);
    } else {
      const activeQuickItem = visibleQuickItems.value.find(itemIsActive);
      if (activeQuickItem) emit('select', activeQuickItem.sectionName || '');
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="vertical-navigation-rail flex h-full w-16 shrink-0 flex-col border-n-weak bg-n-solid-2 ltr:border-r rtl:border-l"
  >
    <div class="grid h-[3.75rem] shrink-0 place-items-center">
      <button
        v-tooltip.right="panelToggleLabel"
        type="button"
        class="grid size-10 place-items-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
        :aria-label="panelToggleLabel"
        :aria-expanded="panelOpen"
        @click="emit('togglePanel')"
      >
        <span class="i-hugeicons-menu-01 size-[1.125rem]" />
      </button>
    </div>

    <nav
      class="rail-scroll min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto pb-3 pt-3"
    >
      <ul class="m-0 flex list-none flex-col items-center gap-2 p-0">
        <li v-for="item in visibleQuickItems" :key="item.name">
          <button
            v-tooltip.right="item.label"
            type="button"
            class="grid size-10 place-items-center rounded-xl transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            :class="
              quickItemIsSelected(item)
                ? 'bg-n-brand text-white shadow-md shadow-n-brand/25 ring-1 ring-n-brand/30'
                : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-brand'
            "
            :aria-label="item.label"
            :aria-current="quickItemIsSelected(item) ? 'page' : undefined"
            @click="openQuickItem(item)"
          >
            <Icon :icon="item.icon" class="size-[1.125rem]" />
          </button>
        </li>
      </ul>

      <div
        v-if="visibleQuickItems.length"
        class="mx-auto mt-4 h-px w-9 bg-n-weak"
      />

      <ul class="m-0 mt-4 flex list-none flex-col items-center gap-3 p-0">
        <li v-for="section in visibleSections" :key="section.name">
          <RouterLink
            v-if="section.direct && section.to"
            v-tooltip.right="section.label"
            :to="section.to"
            class="grid size-10 place-items-center rounded-xl transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            :class="
              sectionIsSelected(section)
                ? 'bg-n-brand text-white shadow-md shadow-n-brand/25 ring-1 ring-n-brand/30'
                : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-brand'
            "
            :aria-label="section.label"
            :aria-current="sectionIsSelected(section) ? 'page' : undefined"
            @click="selectDirectSection"
          >
            <Icon
              v-if="section.icon"
              :icon="section.icon"
              class="size-[1.125rem]"
            />
            <span v-else class="i-hugeicons-circle size-[1.125rem]" />
          </RouterLink>
          <button
            v-else
            v-tooltip.right="section.label"
            type="button"
            class="grid size-10 place-items-center rounded-xl transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            :class="
              sectionIsSelected(section)
                ? 'bg-n-brand text-white shadow-md shadow-n-brand/25 ring-1 ring-n-brand/30'
                : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-brand'
            "
            :aria-label="section.label"
            :aria-pressed="sectionIsSelected(section)"
            @click="selectSection(section)"
          >
            <Icon
              v-if="section.icon"
              :icon="section.icon"
              class="size-[1.125rem]"
            />
            <span v-else class="i-hugeicons-circle size-[1.125rem]" />
          </button>
        </li>
        <li v-if="showSuperAdmin">
          <SuperAdminLink compact />
        </li>
      </ul>
    </nav>
  </div>
</template>

<style scoped>
.rail-scroll {
  scrollbar-gutter: stable both-edges;
  scrollbar-color: transparent transparent;
  scrollbar-width: thin;
}

.vertical-navigation-rail:hover .rail-scroll {
  scrollbar-color: rgba(120, 124, 136, 0.45) transparent;
}
</style>
